import { useState, useCallback, useMemo } from "react";

interface ColorAlphaInputProps {
  /** Color value as hex with alpha: "#rrggbbaa" or "#rrggbb" */
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
}

/** Parse "#rrggbb" or "#rrggbbaa" into { hex6, alpha01 }. */
function parseColor(raw: string): { hex6: string; alpha: number } {
  const s = raw.startsWith("#") ? raw.slice(1) : raw;
  if (s.length === 8) {
    return {
      hex6: `#${s.slice(0, 6)}`,
      alpha: parseInt(s.slice(6, 8), 16) / 255,
    };
  }
  if (s.length === 6) {
    return { hex6: `#${s}`, alpha: 1 };
  }
  if (s.length === 3) {
    const expanded = s[0]! + s[0]! + s[1]! + s[1]! + s[2]! + s[2]!;
    return { hex6: `#${expanded}`, alpha: 1 };
  }
  return { hex6: "#000000", alpha: 1 };
}

/** Combine hex6 + alpha01 into "#rrggbbaa". */
function toHex8(hex6: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, "0");
  const base = hex6.startsWith("#") ? hex6 : `#${hex6}`;
  if (a === "ff") return base; // fully opaque — skip alpha suffix
  return `${base}${a}`;
}

export function ColorAlphaInput({
  value,
  onChange,
  label,
  required,
}: ColorAlphaInputProps) {
  const { hex6, alpha } = useMemo(() => parseColor(value), [value]);
  const [textValue, setTextValue] = useState(value);

  const handleColor = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHex = e.target.value;
      const result = toHex8(newHex, alpha);
      onChange(result);
      setTextValue(result);
    },
    [alpha, onChange],
  );

  const handleAlpha = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const a = Number(e.target.value) / 100;
      const result = toHex8(hex6, a);
      onChange(result);
      setTextValue(result);
    },
    [hex6, onChange],
  );

  const handleText = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextValue(e.target.value);
      const v = e.target.value.trim();
      if (/^#[0-9a-fA-F]{6,8}$/.test(v)) {
        onChange(v);
      }
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    setTextValue(value);
  }, [value]);

  const alphaPercent = Math.round(alpha * 100);

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-2">
        {label}
        {required && <span className="ml-1 text-danger">*</span>}
      </label>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          {/* Color swatch with alpha preview */}
          <div
            className="relative h-9 w-9 overflow-hidden rounded border border-line"
            style={{
              backgroundImage:
                "linear-gradient(45deg, #333 25%, transparent 25%), " +
                "linear-gradient(-45deg, #333 25%, transparent 25%), " +
                "linear-gradient(45deg, transparent 75%, #333 75%), " +
                "linear-gradient(-45deg, transparent 75%, #333 75%)",
              backgroundSize: "8px 8px",
              backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0px",
            }}
          >
            <div
              className="absolute inset-0"
              style={{ backgroundColor: value }}
            />
            <input
              type="color"
              value={hex6}
              onChange={handleColor}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            />
          </div>
          {/* Hex text input */}
          <input
            type="text"
            value={textValue}
            onChange={handleText}
            onBlur={handleBlur}
            placeholder="#rrggbbaa"
            className="flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-line-strong focus:outline-none"
          />
        </div>
        {/* Alpha slider */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-ink-3">Alpha</span>
          <input
            type="range"
            min={0}
            max={100}
            value={alphaPercent}
            onChange={handleAlpha}
            className="flex-1 accent-accent"
          />
          <span className="w-10 text-right text-[10px] text-ink-2">
            {alphaPercent}%
          </span>
        </div>
      </div>
    </div>
  );
}
